import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import * as React from "react";
import { Map } from "schemas/functions";

import AboutFormLinks from "features/Auth/Onboarding/AboutForm/Links";
import { SocialMediaLinks } from "features/Auth/Onboarding/AboutForm/helpers";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { HIDE_ONBOARDING_STEPS } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import { Icon } from "../Gamification/components/shared/icons";
import styles from "./styles";

type StepId = "link" | "template" | "followup" | "first_pitch";

interface Step {
  id: StepId;
  icon: JSX.Element;
  title: string;
  description: string;
  cta: string;
}

interface OnboardingStepperInputs {
  profile: Parameters<typeof hasSocialMediaProfile>[0];
  outreachTemplates: unknown[];
  followupTemplates: unknown[];
  totalEmailsSent: number;
}

const STEPS: Step[] = [
  {
    id: "link",
    icon: <Icon.Link size={16} />,
    title: "Add a Link",
    description:
      "Social link, website, portfolio, company page — anywhere brands can see your work.",
    cta: "Add a link",
  },
  {
    id: "template",
    icon: <Icon.Doc size={16} />,
    title: "Create a pitch template",
    description:
      "Upload your own template or use our tools to help you write one. This email is what brands receive.",
    cta: "Create",
  },
  {
    id: "followup",
    icon: <Icon.Send size={16} />,
    title: "Set up follow-ups",
    description:
      "Most replies come from follow-ups. Set it once and Bento sends them automatically.",
    cta: "Set up",
  },
  {
    id: "first_pitch",
    icon: <Icon.Play size={16} />,
    title: "Send your first pitch",
    description:
      "Pick a brand, tweak your email, and hit send — all through Bento.",
    cta: "Start",
  },
];

export const getVisibleOnboardingSteps = ({
  profile,
  outreachTemplates,
  followupTemplates,
  totalEmailsSent,
}: OnboardingStepperInputs): Step[] => {
  const isStepComplete = (id: StepId) => {
    if (id === "link") return !!hasSocialMediaProfile(profile);
    if (id === "template") return outreachTemplates.length > 0;
    if (id === "followup") return followupTemplates.length > 0;
    return totalEmailsSent > 0;
  };
  return STEPS.filter((s) => !isStepComplete(s.id));
};

export default function OnboardingStepper({
  totalEmailsSent,
}: {
  totalEmailsSent: number;
}) {
  const { currentOrg, profile, setProfile } = React.useContext(
    OrganizationUserContext,
  );
  const {
    setCreateTemplateOnboardingDialogOpen,
    setCampaignBuilderDialogOpen,
  } = React.useContext(OnboardingDialogsContext);
  const {
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
  } = React.useContext(OutreachTemplatesContext);
  const { setAlert } = React.useContext(AlertContext);
  const { getToken } = useAuth();

  const hideKey = `${HIDE_ONBOARDING_STEPS}-${currentOrg?.id}`;
  const [hidden, setHidden] = React.useState<boolean>(
    () => localStorage.getItem(hideKey) === "true",
  );

  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [onboardingForm, setOnboardingForm] = React.useState<Map>({});
  const [links, setLinks] = React.useState<SocialMediaLinks[]>([]);
  const [linksValidation, setLinksValidation] = React.useState<{
    [key in string]: string | undefined;
  }>({});
  const [saving, setSaving] = React.useState(false);

  const visibleSteps = getVisibleOnboardingSteps({
    profile,
    outreachTemplates,
    followupTemplates,
    totalEmailsSent,
  });

  const openLinkDialog = () => {
    if (profile) setOnboardingForm(profile);
    setLinks([]);
    setLinksValidation({});
    setLinkDialogOpen(true);
  };

  const handleSaveLinks = async () => {
    setSaving(true);
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        { ...onboardingForm, page: "OnboardingStepper" },
      );
      setProfile(organizationProfile);
      setAlert("Link saved", "success");
      setLinkDialogOpen(false);
    } catch (error) {
      setAlert(error, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStart = (id: StepId) => {
    trackEvent("Home Page Onboarding Step Get Started Clicked", { step: id });
    if (id === "link") {
      openLinkDialog();
    } else if (id === "template") {
      setCreateTemplateOnboardingDialogOpen(true);
    } else if (id === "followup") {
      setCurrentTemplateContentType(TemplateContentType.FOLLOWUP);
      setCreateTemplateDialogOpen(true);
    } else {
      setCampaignBuilderDialogOpen(true);
    }
  };

  const handleHide = () => {
    trackEvent("Home Page Onboarding Hide Clicked");
    localStorage.setItem(hideKey, "true");
    setHidden(true);
  };

  if (hidden || visibleSteps.length === 0) {
    return null;
  }

  return (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.headerIconCircle}>
          <Icon.Sparkle size={20} />
        </Box>
        <Typography sx={styles.suggestionsCount}>
          {visibleSteps.length} suggestion
          {visibleSteps.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      <Typography sx={styles.headerTitle}>Recommended for you</Typography>
      <Typography sx={styles.headerSub}>
        Set up your profile to get the most out of Bento.
      </Typography>

      <Box sx={styles.recList}>
        {visibleSteps.map((step) => (
          <Box key={step.id} sx={styles.row}>
            <Box sx={styles.rowIconCircle}>{step.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={styles.rowTitle}>{step.title}</Typography>
              <Typography sx={styles.rowDesc}>{step.description}</Typography>
            </Box>
            <Button onClick={() => handleStart(step.id)} sx={styles.rowCta}>
              {step.cta}
            </Button>
          </Box>
        ))}
      </Box>

      <Box sx={styles.footer}>
        <Button onClick={handleHide} sx={styles.dontShowAgain}>
          Don't show again
        </Button>
      </Box>

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add a Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
              Social link, website, portfolio, company page — anywhere brands
              can see your work.
            </Typography>
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            loading={saving}
            variant="contained"
            onClick={handleSaveLinks}
            disableElevation
            disabled={!hasSocialMediaProfile(onboardingForm)}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
